// src/App.js — Code-split routes
import React, { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { HelmetProvider } from 'react-helmet-async';
import 'react-toastify/dist/ReactToastify.css';

// Context
import { AuthProvider } from './contexts/AuthContext';

// Routes
import PrivateRoute from './routes/PrivateRoute';
import MemberRoute from './routes/MemberRoute';

// 일반 방문자 진입로 — 즉시 로드 (랜딩과 1차 흐름)
import Home from './pages/public/Home';
import Demo from './pages/public/Demo';
import OrgSite from './pages/public/OrgSite';

// Components & Styles
import ErrorBoundary from './components/common/ErrorBoundary';
import RouteSeo from './components/common/RouteSeo';
import Loading from './components/common/Loading';
import { serverPageUrl } from './utils/blog';
import './styles/theme.scss';
import './App.scss';

// 그 외 public — lazy (대부분 첫 진입자가 안 가는 페이지)
const OrderForm = lazy(() => import(/* webpackChunkName: "public-extra" */ './pages/public/OrderForm'));
const OrderLookup = lazy(() => import(/* webpackChunkName: "public-extra" */ './pages/public/OrderLookup'));
const NotFound = lazy(() => import(/* webpackChunkName: "public-extra" */ './pages/public/NotFound'));
const Notices = lazy(() => import(/* webpackChunkName: "public-extra" */ './pages/public/Notices'));
const NoticeDetail = lazy(() => import(/* webpackChunkName: "public-extra" */ './pages/public/NoticeDetail'));
const FAQ = lazy(() => import(/* webpackChunkName: "public-extra" */ './pages/public/FAQ'));
const Contact = lazy(() => import(/* webpackChunkName: "public-extra" */ './pages/public/Contact'));
const Terms = lazy(() => import(/* webpackChunkName: "public-extra" */ './pages/public/Terms'));
const Privacy = lazy(() => import(/* webpackChunkName: "public-extra" */ './pages/public/Privacy'));
const Checkout = lazy(() => import(/* webpackChunkName: "public-extra" */ './pages/public/Checkout'));
const PaymentSuccess = lazy(() => import(/* webpackChunkName: "public-extra" */ './pages/public/PaymentSuccess'));
const PaymentFail = lazy(() => import(/* webpackChunkName: "public-extra" */ './pages/public/PaymentFail'));
const EventLanding = lazy(() => import(/* webpackChunkName: "public-extra" */ './pages/public/EventLanding'));
const BusinessSignup = lazy(() => import(/* webpackChunkName: "public-extra" */ './pages/public/BusinessSignup'));

// 회원 — lazy chunk
const Register = lazy(() => import(/* webpackChunkName: "member" */ './pages/member/Register'));
const Login = lazy(() => import(/* webpackChunkName: "member" */ './pages/member/Login'));
const ForgotPassword = lazy(() => import(/* webpackChunkName: "member" */ './pages/member/ForgotPassword'));
const ResetPassword = lazy(() => import(/* webpackChunkName: "member" */ './pages/member/ResetPassword'));
const MyPageLayout = lazy(() => import(/* webpackChunkName: "member" */ './pages/member/MyPageLayout'));
const MyOrders = lazy(() => import(/* webpackChunkName: "member" */ './pages/member/MyOrders'));
const OrderDetail = lazy(() => import(/* webpackChunkName: "member" */ './pages/member/OrderDetail'));
const MyTickets = lazy(() => import(/* webpackChunkName: "member" */ './pages/member/MyTickets'));
const Profile = lazy(() => import(/* webpackChunkName: "member" */ './pages/member/Profile'));
const Inquiries = lazy(() => import(/* webpackChunkName: "member" */ './pages/member/Inquiries'));
const InquiryDetail = lazy(() => import(/* webpackChunkName: "member" */ './pages/member/InquiryDetail'));
const InquiryForm = lazy(() => import(/* webpackChunkName: "member" */ './pages/member/InquiryForm'));
const Notifications = lazy(() => import(/* webpackChunkName: "member" */ './pages/member/Notifications'));
const MyReviews = lazy(() => import(/* webpackChunkName: "member" */ './pages/member/MyReviews'));
const Favorites = lazy(() => import(/* webpackChunkName: "member" */ './pages/member/Favorites'));
const Coupons = lazy(() => import(/* webpackChunkName: "member" */ './pages/member/Coupons'));

// 관리자 — lazy chunk (일반 방문자에게 절대 로드되지 않음)
const AdminLogin = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Login'));
const AdminLayout = lazy(() => import(/* webpackChunkName: "admin" */ './components/admin/AdminLayout'));
const AdminDashboard = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Dashboard'));
const AdminOrders = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Orders'));
const AdminEvents = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Events'));
const AdminEventForm = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/EventForm'));
const AdminProducts = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Products'));
const AdminTimeSlots = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/TimeSlots'));
const AdminTickets = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Tickets'));
const AdminEntrance = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Entrance'));
const AdminNotices = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Notices'));
const AdminFAQ = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/FAQ'));
const AdminInquiries = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Inquiries'));
const AdminReviews = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Reviews'));
const AdminCoupons = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Coupons'));
const AdminUsers = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Users'));
const AdminInvitations = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Invitations'));
const AdminAdmins = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Admins'));
const AdminOrganizations = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Organizations'));
const AdminOrganizationDetail = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/OrganizationDetail'));
const AdminQnA = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/QnA'));
const AdminBlogPosts = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/BlogPosts'));
const AdminConsultations = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Consultations'));
const AdminBlogPostForm = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/BlogPostForm'));

// 초대권 — lazy
const InvitePage = lazy(() => import(/* webpackChunkName: "invite" */ './pages/invite/InvitePage'));

// /events/:slug 진입을 /e/:slug 로 영구 통일 (서버 nginx 가 봇/SEO 차원에서도 301)
const RedirectEventToLanding = () => {
  const { eventId } = useParams();
  return <Navigate to={`/e/${eventId}`} replace />;
};

// /blog 는 서버 렌더링 페이지(nginx → 백엔드). SPA 안에서 <Link to="/blog..."> 로 들어오면
// SPA 404 대신 전체 페이지 로드로 넘긴다. (운영: same-origin /blog, 로컬: 백엔드 포트)
// nginx 에 /blog location 이 아직 없으면 SPA 가 다시 뜨므로, 직전에 넘긴 적이 있으면 반복 새로고침을 멈춘다.
const ServerRenderedPage = () => {
  const { pathname, search } = useLocation();
  const [stuck, setStuck] = React.useState(false);
  useEffect(() => {
    const key = 'gateon:server-page-redirect';
    try {
      const last = Number(sessionStorage.getItem(key) || 0);
      if (Date.now() - last < 2000) {
        sessionStorage.removeItem(key);
        setStuck(true);
        return;
      }
      sessionStorage.setItem(key, String(Date.now()));
    } catch (e) {
      // sessionStorage 를 못 쓰는 환경 — 그대로 이동
    }
    window.location.replace(serverPageUrl(`${pathname}${search}`));
  }, [pathname, search]);
  return stuck ? <NotFound /> : <Loading />;
};

// 페이지 전환 시 최상단으로 스크롤. SPA 가 기본으로 스크롤 위치를 유지하므로
// 다음 페이지가 이전 스크롤 지점에서 시작되어 사용자가 헷갈리던 문제 해소.
// 같은 path 안의 hash 변경 (#section-tickets 등)은 보존하기 위해 hash 만 다를 때는 skip.
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [pathname]);
  return null;
};

function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <AuthProvider>
          <ScrollToTop />
          <RouteSeo />
          <Suspense fallback={<Loading />}>
            <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/demo" element={<Demo />} />
          <Route path="/o/:orgSlug" element={<OrgSite />} />
          <Route path="/events/:eventId" element={<RedirectEventToLanding />} />
          <Route path="/events/:eventId/order" element={<OrderForm />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/fail" element={<PaymentFail />} />
          <Route path="/lookup" element={<OrderLookup />} />
          <Route path="/notices" element={<Notices />} />
          <Route path="/notices/:noticeId" element={<NoticeDetail />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/e/:slug" element={<EventLanding />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/business/signup" element={<BusinessSignup />} />
          <Route path="/blog" element={<ServerRenderedPage />} />
          <Route path="/blog/*" element={<ServerRenderedPage />} />

          {/* Member Auth Routes */}
          <Route path="/register" element={<Register />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Member MyPage Routes */}
          <Route element={<MemberRoute />}>
            <Route path="/mypage" element={<MyPageLayout />}>
              <Route index element={<MyOrders />} />
              <Route path="orders/:orderNo" element={<OrderDetail />} />
              <Route path="tickets" element={<MyTickets />} />
              <Route path="inquiries" element={<Inquiries />} />
              <Route path="inquiries/new" element={<InquiryForm />} />
              <Route path="inquiries/:inquiryId" element={<InquiryDetail />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="reviews" element={<MyReviews />} />
              <Route path="favorites" element={<Favorites />} />
              <Route path="coupons" element={<Coupons />} />
              <Route path="profile" element={<Profile />} />
            </Route>
          </Route>

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route element={<PrivateRoute minLevel={2} />}>
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="events/new" element={<AdminEventForm />} />
              <Route path="events/:id/edit" element={<AdminEventForm />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="time-slots" element={<AdminTimeSlots />} />
              <Route path="tickets" element={<AdminTickets />} />
              <Route path="entrance" element={<AdminEntrance />} />
              <Route path="notices" element={<AdminNotices />} />
              <Route path="faq" element={<AdminFAQ />} />
              <Route path="inquiries" element={<AdminInquiries />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="qna" element={<AdminQnA />} />
              <Route path="coupons" element={<AdminCoupons />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="invitations" element={<AdminInvitations />} />
              <Route element={<PrivateRoute minLevel={5} />}>
                <Route path="admins" element={<AdminAdmins />} />
              </Route>
            </Route>
          </Route>

          {/* Invite Routes (업체용 초대권 페이지) */}
          <Route path="/invite" element={<InvitePage />} />

          {/* Super Admin Routes */}
          <Route path="/super-admin/login" element={<AdminLogin />} />
          <Route element={<PrivateRoute minLevel={9} />}>
            <Route path="/super-admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="events/new" element={<AdminEventForm />} />
              <Route path="events/:id/edit" element={<AdminEventForm />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="time-slots" element={<AdminTimeSlots />} />
              <Route path="tickets" element={<AdminTickets />} />
              <Route path="entrance" element={<AdminEntrance />} />
              <Route path="notices" element={<AdminNotices />} />
              <Route path="faq" element={<AdminFAQ />} />
              <Route path="inquiries" element={<AdminInquiries />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="qna" element={<AdminQnA />} />
              <Route path="coupons" element={<AdminCoupons />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="invitations" element={<AdminInvitations />} />
              <Route path="admins" element={<AdminAdmins />} />
              <Route path="organizations" element={<AdminOrganizations />} />
              <Route path="organizations/:orgId" element={<AdminOrganizationDetail />} />
              {/* 블로그 — 플랫폼 공용 콘텐츠 (백엔드 /admin/blog = get_super_admin) */}
              <Route path="blog" element={<AdminBlogPosts />} />
              <Route path="blog/new" element={<AdminBlogPostForm />} />
              <Route path="blog/:id/edit" element={<AdminBlogPostForm />} />
              {/* 도입 상담 신청 — 플랫폼 영업 데이터 (백엔드 /admin/consultations = get_super_admin) */}
              <Route path="consultations" element={<AdminConsultations />} />
            </Route>
          </Route>

          {/* 404 — 정의되지 않은 모든 경로 (빈 화면 방지) */}
          <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <ToastContainer position="top-right" autoClose={3000} />
        </AuthProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
